import { FC, useState, useEffect } from 'react'
import { Badge, Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import NoActiveContractMessage from 'components/NoActiveContractMessage'
import R4RIcon from 'components/R4RIcon'
import Dashboard from 'layouts/Dashboard'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useBankAccountConfirmationCheck } from 'services/contracts/hooks/useBankAccountConfirmationCheck'
import { useUsersProfile } from 'services/profile/profile.hooks'
import { useNotificationsList } from 'services/notifications/notifications.hook'
import BankAccountConfirmationDialog from 'components/BankAccountConfirmationDialog'
import { UserProtectedRoute } from 'routes'
import {
  WidgetAnnouncements,
  WidgetDiscount,
  WidgetConsultant,
  WidgetContract,
  WidgetFault,
  WidgetBalance,
  WidgetPersonalData
} from 'components/Widgets'

const HomePage: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { formatMessage } = useIntl()
  const { leaseContractsList } = useLeaseContractsList()
  const { data: usersProfile, userInitials } = useUsersProfile()
  const { hasUnread } = useNotificationsList()
  const { shouldShowBankAccountConfirmation, dismissBankAccountConfirmation } = useBankAccountConfirmationCheck()
  const [showBankAccountDialog, setShowBankAccountDialog] = useState(false)

  const activeLeases = leaseContractsList?.filter(({ leaseStatus }) => leaseStatus === 0) || []
  const hasActiveLeases = activeLeases.length > 0

  useEffect(() => {
    if (shouldShowBankAccountConfirmation) {
      setShowBankAccountDialog(true)
    }
  }, [shouldShowBankAccountConfirmation])

  const handleCloseBankAccountDialog = () => {
    setShowBankAccountDialog(false)
    dismissBankAccountConfirmation()
  }

  const greeting = formatMessage(
    { id: 'home.greeting', defaultMessage: 'Dzień dobry, {name}' },
    { name: usersProfile?.firstName || '' }
  )

  const avatar = (size: number) => (
    <IconButton component={Link} to='/user' sx={{ padding: 0 }}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.r4r.gradient,
          color: '#fff',
          fontSize: size >= 42 ? '14px' : '13px',
          fontWeight: 700
        }}>
        {userInitials}
      </Box>
    </IconButton>
  )

  if (isDesktop) {
    return (
      <UserProtectedRoute>
        <Dashboard noHeader disableGutters>
          <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh' }}>
          <Box
            sx={{
              maxWidth: '1220px',
              mx: 'auto',
              px: '36px',
              pb: '64px',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px'
            }}>
            <Typography
              component='h1'
              sx={{ pt: '47px', fontSize: '24px', fontWeight: 800, color: theme.r4r.navy }}>
              {greeting}
            </Typography>
            <WidgetPersonalData />
            <WidgetBalance />
            <NoActiveContractMessage sx={{ display: !hasActiveLeases ? 'block' : 'none' }} />
            <Box sx={{ display: 'flex', gap: '24px', alignItems: 'stretch' }}>
              <WidgetFault />
              <WidgetContract />
            </Box>
            <Box sx={{ display: 'flex', gap: '24px', alignItems: 'stretch' }}>
              <WidgetAnnouncements />
              <WidgetConsultant />
            </Box>
            <WidgetDiscount />
          </Box>
          </Box>
        </Dashboard>
        <BankAccountConfirmationDialog
          isOpen={showBankAccountDialog}
          onClose={handleCloseBankAccountDialog}
        />
      </UserProtectedRoute>
    )
  }

  return (
    <UserProtectedRoute>
      <Dashboard noHeader>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh', pb: '24px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: '20px',
              pt: '14px',
              pb: '8px'
            }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: theme.r4r.navy }}>
              {greeting}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <IconButton
                component={Link}
                to='/notifications'
                sx={{ color: theme.r4r.navy, padding: 0 }}>
                <Badge
                  variant='dot'
                  invisible={!hasUnread}
                  sx={{
                    '.MuiBadge-badge': {
                      backgroundColor: theme.r4r.red
                    }
                  }}>
                  <R4RIcon name='bell' size={24} />
                </Badge>
              </IconButton>
              {avatar(36)}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              px: '16px'
            }}>
            <WidgetPersonalData />
            <WidgetBalance />
            <NoActiveContractMessage sx={{ display: !hasActiveLeases ? 'block' : 'none' }} />
            <WidgetFault />
            <WidgetAnnouncements />
            <WidgetContract />
            <WidgetConsultant />
            <WidgetDiscount />
          </Box>
        </Box>
      </Dashboard>
      <BankAccountConfirmationDialog
        isOpen={showBankAccountDialog}
        onClose={handleCloseBankAccountDialog}
      />
    </UserProtectedRoute>
  )
}

export default HomePage
